import { NumberField } from "@base-ui/react/number-field";
import {
	FormControl,
	FormHelperText,
	InputBaseComponentProps,
	InputLabel,
	OutlinedInput,
	SxProps,
	Theme
} from "@mui/material";
import { ComponentProps, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useId, useRef, useState } from "react";

import numberRangeMessage from "~/components/textfield/numberRangeMessage";

type NumberFieldRootProps = ComponentProps<typeof NumberField.Root>;

// InputBase always passes value/defaultValue (even undefined), which would override Base UI's formatted value
const NumberFieldBaseInput = ({ defaultValue: _defaultValue, value: _value, ...props }: InputBaseComponentProps) => (
	<NumberField.Input {...props} />
);

// − is the Unicode minus sign Base UI also accepts alongside the ASCII hyphen
const preventMinusKey = (event: KeyboardEvent<HTMLInputElement>) => {
	if (event.key === "-" || event.key === "−") {
		event.preventDefault();
	}
};

export interface NumberFieldBaseProps
	extends Pick<
		NumberFieldRootProps,
		| "disabled"
		| "format"
		| "locale"
		| "max"
		| "min"
		| "name"
		| "onValueChange"
		| "onValueCommitted"
		| "readOnly"
		| "required"
		| "step"
	> {
	ariaLabel?: string;
	autoFocus?: boolean;
	dataTestId?: string;
	endAdornment?: ReactNode;
	error?: boolean;
	fullWidth?: boolean;
	helperText?: string;
	id?: string;
	label?: string;
	onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
	placeholder?: string;
	selectOnFocus?: boolean;
	showMinOnlyRangeMessage?: boolean;
	size?: "medium" | "small";
	startAdornment?: ReactNode;
	sx?: SxProps<Theme>;
	value: number | null;
}

const NumberFieldBase = ({
	ariaLabel,
	autoFocus,
	dataTestId,
	endAdornment,
	error,
	fullWidth,
	helperText,
	id,
	label,
	onKeyDown,
	placeholder,
	selectOnFocus,
	showMinOnlyRangeMessage,
	size,
	startAdornment,
	sx,
	value,
	...rootProps
}: NumberFieldBaseProps) => {
	const [focused, setFocused] = useState(false);
	const reselectOnPointerRelease = useRef(false);
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const helperTextId = `${inputId}-helper-text`;
	const shrink = focused || value !== null || startAdornment !== undefined;
	const { max, min } = rootProps;
	const isOutOfRange = value !== null && ((min !== undefined && value < min) || (max !== undefined && value > max));
	const showsRangeMessage = max !== undefined || showMinOnlyRangeMessage;
	const outOfRangeText = showsRangeMessage ? numberRangeMessage(min, max) : undefined;
	const displayedHelperText = isOutOfRange && outOfRangeText !== undefined ? outOfRangeText : helperText;

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (min !== undefined && min >= 0) preventMinusKey(event);
		onKeyDown?.(event);
	};

	const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
		setFocused(true);
		if (!selectOnFocus) return;
		event.target.select();
	};

	const handleMouseDown = (event: MouseEvent<HTMLInputElement>) => {
		reselectOnPointerRelease.current = selectOnFocus === true && document.activeElement !== event.currentTarget;
	};

	const handleClick = (event: MouseEvent<HTMLInputElement>) => {
		if (!reselectOnPointerRelease.current) return;
		reselectOnPointerRelease.current = false;
		event.currentTarget.select();
	};

	return (
		<NumberField.Root
			{...rootProps}
			allowOutOfRange
			render={
				<FormControl
					disabled={rootProps.disabled}
					error={error || isOutOfRange}
					fullWidth={fullWidth}
					required={rootProps.required}
					size={size}
					sx={sx}
				/>
			}
			value={value}
		>
			{label && (
				<InputLabel htmlFor={inputId} shrink={shrink}>
					{label}
				</InputLabel>
			)}
			<OutlinedInput
				autoFocus={autoFocus}
				endAdornment={endAdornment}
				id={inputId}
				inputComponent={NumberFieldBaseInput}
				inputProps={{
					"aria-describedby": displayedHelperText === undefined ? undefined : helperTextId,
					"aria-label": ariaLabel,
					"data-testid": dataTestId,
					onClick: handleClick,
					onKeyDown: handleKeyDown,
					onMouseDown: handleMouseDown
				}}
				label={label}
				notched={Boolean(label) && shrink}
				onBlur={() => {
					setFocused(false);
					reselectOnPointerRelease.current = false;
				}}
				onFocus={handleFocus}
				placeholder={placeholder}
				readOnly={rootProps.readOnly}
				startAdornment={startAdornment}
			/>
			{displayedHelperText !== undefined && (
				// 1lh reserves one themed helper line so toggling to an error message causes no layout shift
				<FormHelperText id={helperTextId} sx={{ minHeight: "1lh" }}>
					{displayedHelperText}
				</FormHelperText>
			)}
		</NumberField.Root>
	);
};

export default NumberFieldBase;
